import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react"
import { router } from "@inertiajs/react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { buttonVariants } from "@/components/ui/button"
import { PII_MASK_CLASS } from "@/components/private"

type ConfirmOptions = {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  /** "destructive" styles the confirm button red (deletions, irreversible actions). */
  variant?: "default" | "destructive"
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

/**
 * Mount once at the app root (see entrypoints/inertia.tsx). Backs the
 * `useConfirm()` hook — a drop-in, promise-based replacement for
 * `window.confirm()` that renders a shadcn AlertDialog instead of the
 * native browser dialog.
 */
export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolveRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts)
    return new Promise((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  function settle(value: boolean) {
    resolveRef.current?.(value)
    resolveRef.current = null
    setOptions(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={options != null}
        onOpenChange={(open) => {
          if (!open) settle(false)
        }}
      >
        {options && (
          <AlertDialogContent>
            <AlertDialogHeader>
              {/* Masked for session replay: confirmation titles in this app
                  routinely name the person being acted on ("Activate Dale
                  Hoffman?"). The description below stays readable, and the
                  action is legible from the buttons either way. */}
              <AlertDialogTitle className={PII_MASK_CLASS}>{options.title}</AlertDialogTitle>
              {options.description && (
                <AlertDialogDescription>{options.description}</AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => settle(false)}>
                {options.cancelLabel ?? "Cancel"}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => settle(true)}
                className={options.variant === "destructive" ? buttonVariants({ variant: "destructive" }) : undefined}
              >
                {options.confirmLabel ?? "Continue"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  )
}

/** Returns a function that opens the shared confirm dialog and resolves to whether the user confirmed. */
export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext)
  if (!confirm) throw new Error("useConfirm must be used within a ConfirmDialogProvider")
  return confirm
}

type ConfirmDeleteOptions = Omit<ConfirmOptions, "variant">

/**
 * The "confirm, then router.delete(path, { preserveScroll: true })" shape
 * repeated across every admin delete/remove action. Returns whether the
 * user confirmed (and the delete request was sent).
 */
export function useConfirmDelete(): (path: string, options: ConfirmDeleteOptions) => Promise<boolean> {
  const confirmDialog = useConfirm()
  return useCallback(
    async (path, options) => {
      const confirmed = await confirmDialog({ ...options, variant: "destructive" })
      if (confirmed) router.delete(path, { preserveScroll: true })
      return confirmed
    },
    [confirmDialog],
  )
}
